import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  CircularProgress,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  ContentCopy as ContentCopyIcon,
  Dns as DnsIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import type { ExecutionBotIp } from '../../types/attacks';
import { useNotification } from '../../contexts/NotificationContext';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

interface ExecutionBotIpsPanelProps {
  executionId: string;
}

function CopyIpButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy IP'}>
      <IconButton
        size="small"
        aria-label={`Copy ${value}`}
        onClick={() => {
          navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          });
        }}
      >
        <ContentCopyIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Tooltip>
  );
}

const ExecutionBotIpsPanel: React.FC<ExecutionBotIpsPanelProps> = ({ executionId }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { showNotification } = useNotification();

  const [bots, setBots] = useState<ExecutionBotIp[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSearch('');
    setPage(0);
    api
      .getExecutionBotIps(executionId)
      .then((data) => {
        if (!cancelled) setBots(data);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load execution bot IPs:', err);
          setBots([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [executionId]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return bots;
    return bots.filter(
      (b) =>
        b.public_ip.toLowerCase().includes(term) ||
        (b.region_name ?? '').toLowerCase().includes(term) ||
        (b.provider_name ?? '').toLowerCase().includes(term)
    );
  }, [bots, search]);

  useEffect(() => {
    setPage(0);
  }, [search]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleCopyAll = () => {
    if (filtered.length === 0) return;
    const text = filtered.map((b) => b.public_ip).join('\n');
    navigator.clipboard
      .writeText(text)
      .then(() => showNotification(`Copied ${filtered.length} IP${filtered.length === 1 ? '' : 's'} to clipboard.`, 'success'))
      .catch(() => showNotification('Failed to copy IPs to clipboard.', 'error'));
  };

  return (
    <Card
      sx={{
        backgroundColor: alpha(theme.palette.background.paper, 0.6),
        backdropFilter: 'blur(12px)',
        border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.15 : 0.2)}`,
        borderRadius: 2,
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.9)}, transparent)`,
        },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.18),
                borderRadius: '10px',
                p: 1,
                border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.3)}`,
                color: theme.palette.primary.main,
                display: 'flex',
              }}
            >
              <DnsIcon sx={{ fontSize: '1.15rem' }} />
            </Box>
            <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 600, color: 'text.primary' }}>
              Bot IPs
            </Typography>
            {!loading && (
              <Chip label={bots.length} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
            )}
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
            onClick={handleCopyAll}
            disabled={filtered.length === 0}
          >
            Copy {search.trim() ? 'filtered' : 'all'} IPs
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 4 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Loading bot IPs…
            </Typography>
          </Box>
        )}

        {!loading && bots.length === 0 && (
          <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
            No bots reported for this execution.
          </Typography>
        )}

        {!loading && bots.length > 0 && (
          <>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by IP, region, or provider…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ mb: 1.5 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {filtered.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                No IPs match your search.
              </Typography>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Public IP</TableCell>
                        <TableCell>Region</TableCell>
                        <TableCell>Provider</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Copy</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paged.map((bot) => (
                        <TableRow key={bot.bot_id} hover>
                          <TableCell sx={{ fontFamily: '"Space Mono", monospace' }}>{bot.public_ip}</TableCell>
                          <TableCell>{bot.region_name ?? '—'}</TableCell>
                          <TableCell>{bot.provider_name ?? '—'}</TableCell>
                          <TableCell>{bot.status ?? '—'}</TableCell>
                          <TableCell align="right">
                            <CopyIpButton value={bot.public_ip} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={filtered.length}
                  page={page}
                  onPageChange={(_e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                />
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ExecutionBotIpsPanel;
